import React, { useEffect, useState, useRef } from 'react';
import Select from 'react-select';
import { Controller, useForm, useWatch } from 'react-hook-form';
import Button from '../Button';
import FormLabel from '../FormLabel';
import FormItem from '../FormItem';

import formStyles from '../form.module.css';

import { listAvailableApps, listGroups } from '../../utilities/api';
import { hasAccessToAllDomainUsers } from '../../utilities/utils';

import Spinner from '../Spinner';

import Modal, { Header, Body } from './Modal';

const NameFieldErrorMessage = ({ errorType }) => (
  <div className={formStyles.errorMessage}>
    {console.log(errorType)}
    {errorType === 'required' && 'Name is required'}
    {errorType === 'validate' && 'Name is already in use'}
    {errorType === 'pattern' &&
      'Name must contain only letters, numbers, spaces and hyphens, and may not start or end with a hyphen.'}
  </div>
);

const validNamePattern = /^[a-z0-9][-a-z0-9 ]*[a-z0-9]$/i;
const validSystemNamePattern = /^[a-z0-9][-a-z0-9]*[a-z0-9]$/;

const PublishApp = ({
  confirm,
  close,
  publishedApps,
  updateApp,
  name,
  displayName,
  image,
  imageTag,
  groups
}) => {
  const [apps, setApps] = useState([]);
  const [groupOptions, setGroupOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const form = useRef(null);
  const { handleSubmit, errors, control, setValue, register, reset } = useForm();

  const app = useWatch({ name: 'app', control });
  const access = useWatch({ name: 'access', control });
  const inputDisplayName = useWatch({ name: 'displayName', control });

  let appName;

  useEffect(() => {
    const fetchApps = async () => {
      const apps = await listAvailableApps();
      setApps(apps);
      setIsLoading(false);

      if (!updateApp) {
        setValue('access', 'all');
      } else {
        reset({
          app: { value: image, label: image },
          tag: { value: imageTag, label: imageTag },
          displayName,
          name,
          access: hasAccessToAllDomainUsers(groups) ? 'all' : 'group'
        });
      }
    };

    const fetchGroups = async () => {
      const list = await listGroups();
      const groupOptions = list.map(g => ({ value: g.id, label: g.name }));
      setGroupOptions(groupOptions);

      if (groups) {
        reset({
          groups: groups
            .map(group => groupOptions.find(x => x.value === group))
            .filter(x => x)
        });
      }
    };

    fetchApps();
    fetchGroups();
  }, [reset, setValue, name, displayName, image, imageTag, groups, updateApp]);

  useEffect(() => {
    setValue('tag', null);
  }, [app, setValue]);

  if (!updateApp) {
    appName = inputDisplayName ? inputDisplayName.toLowerCase().replaceAll(' ', '-') : '';
    setValue('name', appName);
  }

  const tags = apps
    .find(x => x.name === app?.value)?.tags
    .reverse()
    .map(x => ({ value: x, label: x}));

  return (
    <Modal close={close}>
      <Header>{`${updateApp ? 'Update' : 'Publish'} application`}</Header>
      <Body>
        Following applications are available for publishing in the Container Registry
        {isLoading ? (
          <Spinner />
        ) : (
          <form
            className={formStyles.form}
            // Name changes to undefined when handleSubmit.
            // Don't know why, so set it manually
            onSubmit={handleSubmit(data => confirm(data, appName))}
            ref={form}
          >
            <FormItem>
              <FormLabel>Image</FormLabel>
              <Controller
                as={
                  <Select
                    onChange={() => reset('tag')}
                    options={apps.map(x => ({ value: x.name, label: x.name }))}
                    styles={{
                      control: styles => ({
                        ...styles,
                        border: errors.app ? '2px solid var(--error)' : styles.border,
                        height: 46,
                        marginBottom: '0.5em'
                      })
                    }}
                  />
                }
                control={control}
                defaultValue=""
                name="app"
                rules={{ required: true }}
              />
            </FormItem>
            <FormItem>
              <FormLabel>Version tag</FormLabel>
              <Controller
                as={
                  <Select
                    options={tags}
                    styles={{
                      control: styles => ({
                        ...styles,
                        border: errors.tag ? '2px solid var(--error)' : styles.border,
                        height: 46,
                        marginBottom: '0.5em'
                      })
                    }}
                  />
                }
                control={control}
                defaultValue=""
                name="tag"
                rules={{
                  required: true,
                  validate: tag => tags.map(x => x.value).includes(tag.value)
                }}
              />
            </FormItem>
            <FormItem>
              <FormLabel>Name</FormLabel>
              {errors.name && <NameFieldErrorMessage errorType={errors.name.type} />}
              <input
                className={`${formStyles.input} ${errors.name && formStyles.error}`}
                disabled={updateApp}
                name="displayName"
                placeholder="Application name"
                ref={register({
                  required: true,
                  pattern: validNamePattern,
                  validate: name => updateApp || !publishedApps.find(app => app.name === name)
                })}
                type="text"
              />
            </FormItem>
            <FormItem>
              <FormLabel>System name</FormLabel>
              {errors.displayName && <NameFieldErrorMessage errorType={errors.displayName.type} />}
              <input
                className={`${formStyles.input} ${errors.displayName && formStyles.error}`}
                disabled
                name="name"
                placeholder="System name will be auto-generated based on application name"
                ref={register({
                  required: true,
                  pattern: validSystemNamePattern,
                  validate: name => updateApp || !publishedApps.find(app => app.name === name)
                })}
                type="text"
              />
            </FormItem>
            <FormItem>
              <fieldset>
                <legend>Application access</legend>

                <div>
                  <input id="access-all" name="access" ref={register} type="radio" value="all" />
                  <label htmlFor="access-all">Any Orion domain user</label>
                </div>
                <div>
                  <input
                    id="access-group"
                    name="access"
                    ref={register}
                    type="radio"
                    value="group"
                  />
                  <label htmlFor="access-group">Limit access to group</label>
                </div>

                <FormItem style={{ display: access === 'all' ? 'none' : 'block' }}>
                  <FormLabel>Limit access to group</FormLabel>
                  <Controller
                    as={
                      <Select
                        isMulti
                        menuPlacement="top"
                        options={groupOptions}
                        styles={{
                          control: styles => ({
                            ...styles,
                            border: errors.groups ? '2px solid var(--error)' : styles.border,
                            height: 46,
                            marginBottom: '0.5em'
                          })
                        }}
                      />
                    }
                    control={control}
                    defaultValue=""
                    name="groups"
                    rules={{ required: access === 'group' }}
                  />
                </FormItem>
              </fieldset>
            </FormItem>
            <div className={formStyles.formAction}>
              <Button color="primary" disabled={false} type="submit">
                Publish
              </Button>
            </div>
          </form>
        )}
      </Body>
    </Modal>
  );
};

export default PublishApp;
